import { Button, Card, Toggle } from '@lilith-arcana-os/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ReadingRecord, RitualMode } from '@lilith-arcana-os/domain';
import { recommendSpreads } from '@lilith-arcana-os/spread-registry/derived/recommendations';

import { DrawnCardsPanel } from '../components/reading/DrawnCardsPanel';
import { PageHeader } from '../components/ui/PageHeader';
import { apiClient } from '../lib/api-client';
import { performDrawAuto } from '../lib/entropy-client';
import { bootstrapDeckIds, bootstrapSpreadDefinitions } from '../lib/constants';
import { useReadingStore } from '../stores/reading-store';
import { useUserStore } from '../stores/user-store';

const modes: RitualMode[] = ['standard', 'lilith', 'qliphotic', 'kabbalistic'];

export function ReadingPage() {
  const queryClient = useQueryClient();
  const session = useUserStore((state) => state.session);
  const {
    combinations,
    drawnCards,
    entropyAudit,
    finishDrawing,
    finishInterpreting,
    interpretations,
    isDrawing,
    isInterpreting,
    llmResponse,
    mode,
    question,
    selectedSpread,
    setMode,
    setQuestion,
    setSelectedSpread,
    startDrawing,
    startInterpreting,
    symbolicSummary,
    synthesis,
  } = useReadingStore();
  const recommendations = recommendSpreads({
    desiredCards: selectedSpread.spread.cards,
    mode,
    ritual: Boolean(selectedSpread.spread.ritual_note),
  });
  const currentRecommendation =
    recommendations.find((recommendation) => recommendation.spread_slug === selectedSpread.spread.slug) ?? null;

  const drawMutation = useMutation({
    mutationFn: (request: { spreadId: string; question: string; mode: RitualMode }) =>
      performDrawAuto({
        request,
        deck: bootstrapDeckIds,
        preferHosted: true,
        allowOfflineFallback: true,
      }),
    onMutate: startDrawing,
    onSuccess: finishDrawing,
  });

  const interpretMutation = useMutation({
    mutationFn: apiClient.interpret,
    onMutate: startInterpreting,
    onSuccess: (response) =>
      finishInterpreting({
        combinations: response.combinations,
        interpretations: response.interpretations,
        llmResponse: response.llmResponse,
        symbolicSummary: response.synthesis,
        synthesis: response.synthesis,
      }),
  });

  const saveMutation = useMutation({
    mutationFn: (record: ReadingRecord) => apiClient.saveReadingRecord(record),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['history'] });
      await queryClient.invalidateQueries({ queryKey: ['memory'] });
    },
  });

  const handleDraw = () => {
    void drawMutation.mutate({
      spreadId: selectedSpread.spread.id,
      question,
      mode,
    });
  };

  const handleInterpret = () => {
    if (drawnCards.length === 0) {
      return;
    }

    void interpretMutation.mutate({
      spread: selectedSpread.spread,
      cards: drawnCards,
      question,
      mode,
      withLLM: true,
    });
  };

  const handleSave = () => {
    if (drawnCards.length === 0 || !entropyAudit || interpretations.length === 0) {
      return;
    }

    void saveMutation.mutate({
      id: crypto.randomUUID(),
      user_id: String(session.wpUserId ?? 0),
      spread_id: selectedSpread.spread.id,
      spread_name: selectedSpread.spread.name,
      question,
      intent: null,
      cards: drawnCards,
      interpretation: {
        cards: interpretations,
        combinations,
        triads: [],
        elemental_balance: {
          Fuoco: drawnCards.filter((card) => card.element === 'Fuoco').length,
          Acqua: drawnCards.filter((card) => card.element === 'Acqua').length,
          Aria: drawnCards.filter((card) => card.element === 'Aria').length,
          Terra: drawnCards.filter((card) => card.element === 'Terra').length,
        },
        suit_distribution: {
          Bastoni: drawnCards.filter((card) => card.suit === 'Bastoni').length,
          Coppe: drawnCards.filter((card) => card.suit === 'Coppe').length,
          Spade: drawnCards.filter((card) => card.suit === 'Spade').length,
          Denari: drawnCards.filter((card) => card.suit === 'Denari').length,
        },
        major_minor_ratio: {
          major: drawnCards.filter((card) => card.arcana === 'major').length,
          minor: drawnCards.filter((card) => card.arcana === 'minor').length,
          court: drawnCards.filter((card) => card.court).length,
        },
        dominant_suit: drawnCards.find((card) => card.suit !== null)?.suit ?? null,
        dominant_element: drawnCards[0]?.element ?? null,
        dominant_number: drawnCards[0]?.number ?? null,
        dominant_sephirah: drawnCards.find((card) => card.sephirah !== null)?.sephirah ?? null,
        numerological_patterns: drawnCards.map((card) => card.numerology_meaning),
        zodiacal_patterns: drawnCards.flatMap((card) =>
          card.zodiac_sign ? [`${card.name}: ${card.zodiac_sign}`] : [],
        ),
        decanal_sequence: null,
        modality_balance: {
          Cardinale: drawnCards.filter((card) => card.zodiac_modality === 'Cardinale').length,
          Fisso: drawnCards.filter((card) => card.zodiac_modality === 'Fisso').length,
          Mutabile: drawnCards.filter((card) => card.zodiac_modality === 'Mutabile').length,
        },
        synthesis,
        lilith_narrative: mode === 'lilith' ? symbolicSummary : null,
        kabbalistic_narrative: mode === 'kabbalistic' ? symbolicSummary : null,
        qliphotic_narrative: mode === 'qliphotic' ? symbolicSummary : null,
        dignity_summary: null,
        temporal_range: null,
      },
      entropy_audit: entropyAudit,
      llm_response: llmResponse ?? null,
      llm_backend: 'symbolic_only',
      mode,
      notes: '',
      tags: [],
      is_favorite: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        badge={selectedSpread.spread.name}
        description="La UI invia solo input e mostra output. Draw e interpretazione passano sempre per le REST route del plugin."
        eyebrow="Nuova lettura"
        title="Ritual reading"
      />

      <Card eyebrow="Prompt" title="Domanda rituale">
        <div className="grid gap-4">
          <textarea
            className="min-h-32 rounded-[1.5rem] border border-white/10 bg-[rgba(10,10,15,0.9)] px-4 py-4 text-base text-[var(--la-text)] outline-none transition focus:border-[var(--la-gold)]"
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Scrivi la domanda che apre il varco."
            value={question}
          />

          <label className="grid gap-2 text-sm text-[var(--la-text-muted)]">
            Spread
            <select
              className="rounded-2xl border border-white/10 bg-[rgba(10,10,15,0.9)] px-4 py-3 text-[var(--la-text)]"
              onChange={(event) => {
                const nextSpread = bootstrapSpreadDefinitions.find((spread) => spread.spread.id === event.target.value);
                if (nextSpread) {
                  setSelectedSpread(nextSpread);
                }
              }}
              value={selectedSpread.spread.id}
            >
              {bootstrapSpreadDefinitions.map((spread) => (
                <option key={spread.spread.id} value={spread.spread.id}>
                  {spread.spread.name} · {spread.spread.cards} carte · {spread.spread.difficulty}
                </option>
              ))}
            </select>
          </label>

          <div className="grid gap-2 rounded-[1.5rem] border border-white/10 bg-white/5 p-4 text-sm text-[var(--la-text-muted)]">
            <p>
              {selectedSpread.spread.description}
            </p>
            <p>
              Family: {selectedSpread.family} · Intent: {selectedSpread.intent.join(', ')} · Layout: {selectedSpread.layout_id}
            </p>
            <p>
              Score statico suggerimento: {currentRecommendation?.score ?? 0} · Intensità rituale: {selectedSpread.ritual_intensity}/5
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {modes.map((entry) => (
              <Toggle key={entry} onPressedChange={() => setMode(entry)} pressed={mode === entry}>
                {entry}
              </Toggle>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button disabled={!question || isDrawing} onClick={handleDraw}>
              {isDrawing ? 'Drawing…' : 'Estrai carte'}
            </Button>
            <Button
              disabled={drawnCards.length === 0 || isInterpreting}
              onClick={handleInterpret}
              variant="secondary"
            >
              {isInterpreting ? 'Interpreting…' : 'Interpreta'}
            </Button>
            <Button
              disabled={interpretations.length === 0 || saveMutation.isPending}
              onClick={handleSave}
              variant="ghost"
            >
              {saveMutation.isPending ? 'Saving…' : 'Salva in archivio'}
            </Button>
          </div>
        </div>
      </Card>

      <DrawnCardsPanel cards={drawnCards} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card eyebrow="Interpretazione" title="Synthesis">
          <p className="text-sm leading-relaxed text-[var(--la-text-muted)]">
            {synthesis || 'La sintesi apparira dopo il passaggio /interpret.'}
          </p>
        </Card>
        <Card eyebrow="Combinazioni" title="Pattern">
          <div className="grid gap-3">
            {combinations.length > 0 ? (
              combinations.map((combination) => (
                <div
                  key={`${combination.cards[0].id}-${combination.cards[1].id}`}
                  className="rounded-2xl bg-white/5 p-3"
                >
                  <p className="font-heading text-base text-[var(--la-text)]">
                    {combination.cards[0].name} + {combination.cards[1].name}
                  </p>
                  <p className="text-sm text-[var(--la-text-muted)]">
                    {combination.interaction.description}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-[var(--la-text-muted)]">
                Le combinazioni arriveranno dal service layer dopo l&apos;interpretazione.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
